/**
 * ELO Rating System Utilities
 * 
 * This module provides functions for calculating and updating ELO ratings
 * based on game results between players.
 */

import supabase from './supabaseClient';

/**
 * Calculate new ELO ratings for all players in a game
 * @param {Array} currentElos - Array of current ELO ratings for all 4 players [team1player1, team1player2, team2player1, team2player2]
 * @param {Array} scores - Array of team scores [team1Score, team2Score]
 * @param {Array} playerIds - Array of player IDs [team1player1Id, team1player2Id, team2player1Id, team2player2Id]
 * @param {Boolean} nudie - Whether the game was a "nudie" (special game type)
 * @returns {Promise<Array>} - Array of new ELO ratings for all 4 players
 */
export const calculateEloChange = async (currentElos, scores, playerIds, nudie = false) => {
  // Default starting ELO if player doesn't have one yet
  const defaultElo = 1000;
  
  // Ensure all players have an ELO rating
  const playerElos = currentElos.map(elo => elo || defaultElo);
  
  // Calculate expected values for each player against each opponent
  const exp = calcExp(playerElos);
  const exp1 = (exp[0] + exp[1]) / 2;
  const exp2 = 1 - exp1;
  
  const expteam1 = exp1 / (exp1 + exp2);
  const expteam2 = exp2 / (exp1 + exp2);
  
  // Calculate team differential factors
  const teamDiff = calculateTeamDifferential(playerElos);
  
  // Get game decay factors for each player
  const p1 = await getGameDecayFactor(playerIds[0]);
  const p2 = await getGameDecayFactor(playerIds[1]);
  const p3 = await getGameDecayFactor(playerIds[2]);
  const p4 = await getGameDecayFactor(playerIds[3]);
  
  // Determine win value
  let w = 0;
  if (scores[0] > scores[1]) {
    w = 1;
  }
  
  // Base K-factor
  const k = 20;
  
  // Calculate point differential factor
  const pointFactor = 2 + Math.pow((Math.log10(Math.abs(scores[0] - scores[1])) + 1), 3);
  
  // Calculate ELO changes for each team
  const elochange1 = k * pointFactor * (w - expteam1);
  const elochange2 = k * pointFactor * ((1 - w) - expteam2);
  
  // Calculate new ELO ratings with team differential adjustments
  const newElos = [];
  
  // Team 1 adjustments
  if (playerElos[0] > playerElos[1]) {
    newElos.push(playerElos[0] + (elochange1) * p1 * (1 + teamDiff[0]));
    newElos.push(playerElos[1] + (elochange1) * p2 * (1 - teamDiff[0]));
  } else {
    newElos.push(playerElos[0] + (elochange1) * p1 * (1 - teamDiff[0]));
    newElos.push(playerElos[1] + (elochange1) * p2 * (1 + teamDiff[0]));
  }
  
  // Team 2 adjustments
  if (playerElos[2] > playerElos[3]) {
    newElos.push(playerElos[2] + (elochange2) * p3 * (1 + teamDiff[1]));
    newElos.push(playerElos[3] + (elochange2) * p4 * (1 - teamDiff[1]));
  } else {
    newElos.push(playerElos[2] + (elochange2) * p3 * (1 - teamDiff[1]));
    newElos.push(playerElos[3] + (elochange2) * p4 * (1 + teamDiff[1]));
  }
  
  // Apply nudie bonus/penalty
  if (nudie === 1) {
    newElos[0] -= 30;
    newElos[1] -= 30;
    newElos[2] += 30;
    newElos[3] += 30;
  } else if (nudie === 2) {
    newElos[0] += 30;
    newElos[1] += 30;
    newElos[2] -= 30;
    newElos[3] -= 30;
  }
  
  // Ensure minimum ELO of 100
  return newElos.map(elo => Math.max(Math.round(elo), 100));
};

/**
 * Calculate expected values based on maxdif
 * @param {Array} curr - Current ELO ratings
 * @returns {Array} - Expected values for each player
 */
const calcExp = (curr) => {
  const exp = [];
  const maxdif = 400;
  
  exp.push(((1 / (1 + Math.pow(10, (curr[2] - curr[0]) / maxdif))) + 
            (1 / (1 + Math.pow(10, (curr[3] - curr[0]) / maxdif)))) / 2);
            
  exp.push(((1 / (1 + Math.pow(10, (curr[2] - curr[1]) / maxdif))) + 
            (1 / (1 + Math.pow(10, (curr[3] - curr[1]) / maxdif)))) / 2);
            
  exp.push(((1 / (1 + Math.pow(10, (curr[0] - curr[2]) / maxdif))) + 
            (1 / (1 + Math.pow(10, (curr[1] - curr[2]) / maxdif)))) / 2);
            
  exp.push(((1 / (1 + Math.pow(10, (curr[0] - curr[3]) / maxdif))) + 
            (1 / (1 + Math.pow(10, (curr[1] - curr[3]) / maxdif)))) / 2);
            
  return exp;
};

/**
 * Calculate team differential factors
 * @param {Array} curr - Current ELO ratings
 * @returns {Array} - Team differential factors
 */
const calculateTeamDifferential = (curr) => {
  let big1 = curr[0];
  let small1 = curr[1];
  
  if (big1 < small1) {
    const temp = big1;
    big1 = small1;
    small1 = temp;
  }
  
  let big2 = curr[2];
  let small2 = curr[3];
  
  if (big2 < small2) {
    const temp = big2;
    big2 = small2;
    small2 = temp;
  }
  
  const td_1 = ((big1 - small1) / big1) / 4;
  const td_2 = ((big2 - small2) / big2) / 4;
  
  return [td_1, td_2];
};

/**
 * Get game decay factor for a player based on experience
 * This determines how much a player's ELO changes based on their experience
 * @param {String} playerId - Player ID
 * @returns {Promise<Number>} - Game decay factor
 */
const getGameDecayFactor = async (playerId) => {
  // Fetch the player's game count from the database
  const gameCount = await getPlayerGameCount(playerId) || 0;
  
  const alpha = 0.5; // Starting with a 50% increase in volatility for new players
  const beta = 10;   // Base of the logarithm
  const cop = 1;     // Ensures we don't start with a logarithm of 0
  
  if (gameCount + cop <= 0) {
    return 1 + alpha; // Max damping factor for a brand new player
  }
  
  const dampingFactor = 1 + (alpha / (Math.log10(beta * gameCount + cop) + 1));
  return Math.max(dampingFactor, 1);
};

/**
 * Get player game count from the database
 * @param {String} playerId - Player ID
 * @returns {Promise<Number>} - Number of games played by the player
 */
const getPlayerGameCount = async (playerId) => {
  try {
    // Fetch the player's game count from the players table
    const { data, error } = await supabase
      .from('players')
      .select('game_count')
      .eq('id', playerId)
      .single();
    
    if (error) {
      console.error('Error fetching player game count:', error.message);
      return 0; // Default to 0 in case of error
    }
    
    return data?.game_count || 0;
  } catch (err) {
    console.error('Unexpected error fetching player game count:', err.message);
    return 0; // Default to 0 in case of unexpected error
  }
};

/**
 * Sort players by ELO rating in descending order
 * @param {Array} players - Array of player objects with elo property
 * @returns {Array} - Sorted array of players
 */
export const sortPlayersByElo = (players) => {
  return [...players].sort((a, b) => b.elo - a.elo);
};